import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Box, Button, Icon, Input, Modal, TextArea, Typography } from 'components'
import { UploadHint } from 'components/UploadHint'
import { useApp } from 'store/app/hooks'
import { useAuth } from 'store/auth/hooks'
import { CreateCommunityInput } from './types'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { getCommunityDetailRoute } from 'settings/constants'
import { getAccessToken } from '@privy-io/react-auth'
import { handleUploadError } from 'utils/uploadErrorHandler'
import { track } from 'utils/analytics'
import { getStoredReferral } from 'utils/referralStorage'

export const CreateCommunityModal = () => {
  const { isCreateCommunityModalOpen, setIsCreateCommunityModalOpen } = useApp()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
  } = useForm<CreateCommunityInput>({
    defaultValues: {
      visibility: 'public',
      invite_only: false,
    }
  })

  const visibility = watch('visibility')

  // Log validation errors for debugging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Validation errors:', errors)
    }
  }, [errors])

  useEffect(() => {
    reset({
      community_name: '',
      community_url: '',
      community_description: '',
      visibility: 'public',
      invite_only: false,
      tags: '',
      avatar: null as any,
      cover_photo: null as any,
      // Prefilled from a /r/:ref or ?ref= visit (Community Referral Program)
      referral_code: getStoredReferral() ?? '',
      social: {
        discord: '',
        twitter: '',
        website: '',
        twitch: '',
        youtube: '',
      },
    })
  }, [isCreateCommunityModalOpen, reset])

  const onSubmit = useCallback(
    async (data: CreateCommunityInput) => {
      console.log('=== FORM SUBMITTED ===')
      console.log('Form data:', data)
      console.log('User:', user)

      if (!user) {
        toast.error('You must be logged in to create a community')
        return
      }

      setIsCreating(true)

      try {
        const formData = new FormData()
        formData.append('community_name', data.community_name)
        formData.append('community_url', data.community_url.toLowerCase().replace(/\s+/g, '-'))
        formData.append('community_description', data.community_description)
        formData.append('visibility', data.visibility)
        formData.append('invite_only', String(data.invite_only))
        if (data.referral_code?.trim()) {
          formData.append('referral_code', data.referral_code.trim())
        }

        console.log('FormData contents:')
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1])
        }

        if (data.tags) {
          const tagsArray = data.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
          formData.append('tags', JSON.stringify(tagsArray))
        }

        if (data.avatar && data.avatar.size > 0) {
          formData.append('avatar', data.avatar)
        }

        if (data.cover_photo && data.cover_photo.size > 0) {
          formData.append('cover_photo', data.cover_photo)
        }

        if (data.social) {
          formData.append('social', JSON.stringify(data.social))
        }

        const token = await getAccessToken()
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1'

        console.log('Sending request to:', `${apiUrl}/community/create`)
        console.log('Token:', token ? 'Present' : 'Missing')

        if (!token) {
          toast.error('Authentication token not found. Please log in again.')
          return
        }

        const response = await axios.post(
          `${apiUrl}/community/create`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        if (response.data.success) {
          toast.success('Community created successfully!')
          track('community_created', { community_name: data.community_name })
          setIsCreateCommunityModalOpen(false)
          // Navigate using the community URL instead of ID
          navigate(getCommunityDetailRoute(response.data.community.community_url))
          reset()
        }
      } catch (error: any) {
        console.error('Failed to create community:', error)
        handleUploadError(error, 'Failed to create community')
      } finally {
        setIsCreating(false)
      }
    },
    [user, navigate, reset, setIsCreateCommunityModalOpen]
  )

  return (
    <Modal
      title="Create a new community"
      isOpened={isCreateCommunityModalOpen}
      onClose={() => setIsCreateCommunityModalOpen(false)}
    >
      <Box col className="max-h-[80vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Validation Errors Summary */}
          {Object.keys(errors).length > 0 && (
            <Box className="mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
              <Typography variant="caption" className="text-red-500">
                Please fix the following errors:
              </Typography>
              <ul className="mt-2 space-y-1">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>
                    <Typography variant="caption" className="text-red-400">
                      • {field.replace(/_/g, ' ')}: {error?.message || 'Required field'}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          {/* Cover Photo and Avatar */}
          <Box align="start" row>
            <Box className="basis-2/3" col>
              <Controller
                control={control}
                name="cover_photo"
                render={({ field }) => (
                  <Box
                    className="w-full aspect-[4/1] relative rounded-lg overflow-hidden"
                    justifyCenter
                    alignCenter
                  >
                    <img
                      src={
                        field.value && field.value.size > 0
                          ? URL.createObjectURL(field.value)
                          : undefined
                      }
                      className="w-full h-full object-cover bg-gradient-to-br from-purple-600 to-blue-600"
                      alt="Cover"
                    />
                    <label
                      htmlFor="cover-upload"
                      className="absolute inset-0 z-10 flex justify-center items-center cursor-pointer bg-opacity-50 bg-black hover:opacity-80"
                    >
                      <Box className="w-[44px] h-[44px] rounded-full bg-gradient-primary-dark flex items-center justify-center">
                        <Icon name="camera" />
                      </Box>
                      <input
                        accept="image/*"
                        className="hidden"
                        id="cover-upload"
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            field.onChange(file)
                          }
                        }}
                      />
                    </label>
                  </Box>
                )}
              />

              {/* Community Name */}
              <Controller
                control={control}
                name="community_name"
                rules={{
                  required: 'Community name is required',
                  minLength: {
                    value: 3,
                    message: 'Community name must be at least 3 characters'
                  }
                }}
                render={({ field }) => (
                  <Box col className="mt-4">
                    <Typography variant="caption" className="mb-2">Community Name</Typography>
                    <Input
                      placeholder="Enter community name"
                      error={errors.community_name?.message}
                      {...field}
                    />
                  </Box>
                )}
              />

              {/* Community URL */}
              <Controller
                control={control}
                name="community_url"
                rules={{
                  required: 'Community URL is required',
                  pattern: {
                    value: /^[a-zA-Z0-9-_]+$/,
                    message: 'URL handle can only contain letters, numbers, hyphens and underscores'
                  },
                  minLength: {
                    value: 3,
                    message: 'URL handle must be at least 3 characters'
                  },
                  maxLength: {
                    value: 30,
                    message: 'URL handle must be less than 30 characters'
                  }
                }}
                render={({ field }) => (
                  <Box col className="mt-4">
                    <Typography variant="caption" className="mb-2">Community Handle</Typography>
                    <Input
                      placeholder="my-awesome-community"
                      error={errors.community_url?.message}
                      {...field}
                      onChange={(e) => {
                        // Remove any special characters except hyphens and underscores
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-_]/g, '')
                          .replace(/\s+/g, '-')
                        field.onChange(value)
                      }}
                    />
                    <Typography variant="caption" className="text-gray-400 mt-1 ml-1">
                      This will be your community's unique URL: /community/{field.value || 'your-handle'}
                    </Typography>
                  </Box>
                )}
              />
            </Box>

            {/* Avatar */}
            <Box className="basis-1/3 ml-4" col>
              <Controller
                control={control}
                name="avatar"
                render={({ field }) => (
                  <Box className="relative">
                    <img
                      src={
                        field.value && field.value instanceof File && field.value.size > 0
                          ? URL.createObjectURL(field.value)
                          : '/placeholder-avatar.png'
                      }
                      className="w-32 h-32 object-cover rounded-full"
                      alt="Avatar"
                    />
                    <label
                      htmlFor="avatar-upload"
                      className="absolute inset-0 z-10 flex justify-center items-center cursor-pointer bg-opacity-50 bg-black hover:opacity-80 rounded-full"
                    >
                      <Box className="w-[44px] h-[44px] rounded-full bg-gradient-primary-dark flex items-center justify-center">
                        <Icon name="camera" />
                      </Box>
                      <input
                        accept="image/*"
                        className="hidden"
                        id="avatar-upload"
                        type="file"
                        onChange={(e) => {
                          console.log('Avatar file selected:', e.target.files?.[0])
                          const file = e.target.files?.[0]
                          if (file) {
                            field.onChange(file)
                          }
                        }}
                      />
                    </label>
                    {errors.avatar && (
                      <Typography variant="caption" className="text-red-500 mt-1 block">
                        {errors.avatar.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            <UploadHint type="banner" />
            <UploadHint type="avatar" />
          </div>

          {/* Community Description */}
          <Controller
            control={control}
            name="community_description"
            rules={{
              required: 'Community description is required',
              minLength: {
                value: 10,
                message: 'Description must be at least 10 characters'
              }
            }}
            render={({ field }) => (
              <Box col className="mt-4">
                <Typography variant="caption" className="mb-2">Community Description</Typography>
                <TextArea
                  placeholder="Describe your community..."
                  error={errors.community_description?.message}
                  rows={4}
                  {...field}
                />
              </Box>
            )}
          />

          {/* Tags */}
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <Box col className="mt-4">
                <Typography variant="caption" className="mb-2">Tags (comma separated)</Typography>
                <Input
                  placeholder="gaming, esports, competitive"
                  {...field}
                />
              </Box>
            )}
          />

          {/* Visibility Settings */}
          <Box className="mt-4" col>
            <Typography variant="h5" className="mb-2">Community Settings</Typography>

            <Box row alignCenter className="space-x-4 mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="public"
                  {...register('visibility')}
                  className="mr-2"
                />
                <Typography>Public</Typography>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="private"
                  {...register('visibility')}
                  className="mr-2"
                />
                <Typography>Private</Typography>
              </label>
            </Box>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                {...register('invite_only')}
                className="mr-2"
              />
              <Typography>Invite only (requires invitation to join)</Typography>
            </label>
          </Box>

          {/* Referral code — Community Referral Program. Locks this community to the
              person who referred you; they earn $LEET once your first event runs. */}
          <Box className="mt-4" col>
            <Typography variant="h5" className="mb-2">Referral code (optional)</Typography>
            <Input
              placeholder="Code or username of whoever invited you"
              {...register('referral_code', { maxLength: { value: 64, message: 'Too long' } })}
              error={errors.referral_code?.message}
            />
            <Typography variant="caption" className="text-gray-400 mt-1 ml-1">
              Credits the person who brought your community to Cyberlete. It can't be changed later.
            </Typography>
          </Box>

          {/* Social Links */}
          <Box className="mt-4" col>
            <Typography variant="h5" className="mb-2">Social Links (optional)</Typography>

            <Input
              className="mt-2"
              placeholder="Discord invite link"
              {...register('social.discord')}
              icon="discord"
            />
            <Input
              className="mt-2"
              placeholder="Twitter/X handle"
              {...register('social.twitter')}
              icon="twitter"
            />
            <Input
              className="mt-2"
              placeholder="Website URL"
              {...register('social.website')}
              icon="globe"
            />
            <Input
              className="mt-2"
              placeholder="Twitch channel"
              {...register('social.twitch')}
            />
            <Input
              className="mt-2"
              placeholder="YouTube channel"
              {...register('social.youtube')}
            />
          </Box>

          {/* Action Buttons */}
          <Box className="mt-6" row justify="end">
            <Button
              variant="tint"
              buttonType="button"
              onClick={() => setIsCreateCommunityModalOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              className="ml-4"
              variant="primary"
              buttonType="submit"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Icon spin name="loader" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Community'
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}